// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppShell from './components/layout/AppShell';

// Pages
import LoginPage                from './pages/LoginPage';
import ForgotPasswordPage       from './pages/ForgotPasswordPage';
import ResetPasswordPage        from './pages/ResetPasswordPage';
import DashboardPage            from './pages/DashboardPage';
import MembersPage              from './pages/MembersPage';
import MemberDetailPage         from './pages/MemberDetailPage';
import ContactsPage             from './pages/ContactsPage';
import ContactDetailPage        from './pages/ContactDetailPage';
import ContactTagsSettingsPage  from './pages/ContactTagsSettingsPage';
import EventsPage               from './pages/EventsPage';
import EventDetailPage          from './pages/EventDetailPage';
import InvoicesPage             from './pages/InvoicesPage';
import NewInvoicePage           from './pages/NewInvoicePage';
import InvoiceDetailPage        from './pages/InvoiceDetailPage';
import ProductsPage             from './pages/ProductsPage';
import BookkeepingPage          from './pages/BookkeepingPage';
import BookkeepingReportsPage   from './pages/BookkeepingReportsPage';
import TasksPage                from './pages/TasksPage';
import ReportsPage              from './pages/ReportsPage';
import ReportBuilderPage        from './pages/ReportBuilderPage';
import RegistrationFormSettingsPage from './pages/RegistrationFormSettingsPage';
import PromoCodesPage              from './pages/PromoCodesPage';
import EventDisplayPage            from './pages/EventDisplayPage';
import TransactionsPage            from './pages/TransactionsPage';
import RenewalsPage                from './pages/RenewalsPage';
import RenewalBatchPage            from './pages/RenewalBatchPage';
import RenewalTemplatesPage        from './pages/RenewalTemplatesPage';
import EmailBlastsPage          from './pages/EmailBlastsPage';
import EmailTemplatesPage       from './pages/EmailTemplatesPage';
import LettersPage              from './pages/LettersPage';
import ImportsPage              from './pages/ImportsPage';
import SettingsPage             from './pages/SettingsPage';
import StaffPage                from './pages/StaffPage';
import PermissionsPage          from './pages/PermissionsPage';
import PublicEventsPage         from './pages/PublicEventsPage';
import PublicEventDetailPage    from './pages/PublicEventDetailPage';
import { ThankYouPage }         from './pages/PublicEventDetailPage'; // [marker: confirm-polish v1]
import JoinPage                 from './pages/JoinPage';
import IframeHeightReporter     from './components/IframeHeightReporter';
import PublicJobsPage           from './pages/PublicJobsPage';     // [marker: finish-up v1]
import PortalJobsPage           from './pages/PortalJobsPage';
import JobBoardAdminPage        from './pages/JobBoardAdminPage';
import SponsorLinksPage         from './pages/SponsorLinksPage';    // [marker: fixes-oct21 v1]
import BadgeDesignerPage        from './pages/BadgeDesignerPage';   // [marker: back-tags v1]
import CheckInKioskPage         from './pages/CheckInKioskPage';
import GuestNamesPage           from './pages/GuestNamesPage';   // [marker: guest-names v1]
import WebsitePhotosPage        from './pages/WebsitePhotosPage'; // [marker: website-feed v1]
import TicketPage               from './pages/TicketPage';        // [marker: website-feed v1]

// Member portal
import { PortalAuthProvider }   from './context/PortalAuthContext';
import PortalShell              from './components/layout/PortalShell';
import PortalLoginPage          from './pages/PortalLoginPage';
import PortalForgotPasswordPage from './pages/PortalForgotPasswordPage';
import PortalSetPasswordPage    from './pages/PortalSetPasswordPage';
import PortalDashboard          from './pages/PortalDashboard';
import PortalProfilePage        from './pages/PortalProfilePage';
import PortalInvoicesPage       from './pages/PortalInvoicesPage';
import PortalInvoiceDetailPage  from './pages/PortalInvoiceDetailPage';
import PortalEventsPage         from './pages/PortalEventsPage';
import PortalTicketsPage        from './pages/PortalTicketsPage';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="loading-page" style={{ minHeight: '100vh' }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
      <span>Loading...</span>
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
}

function RequireSuperAdmin({ children }) {
  const { user, isSuperAdmin } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isSuperAdmin()) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <PortalAuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
          <Routes>
            <Route path="/login"            element={<LoginPage />} />
            <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
            <Route path="/reset-password"   element={<ResetPasswordPage />} />
            <Route path="/embed/events"     element={<><IframeHeightReporter /><PublicEventsPage /></>} />
            <Route path="/embed/events/:id" element={<><IframeHeightReporter /><PublicEventDetailPage /></>} />
            <Route path="/join"             element={<><IframeHeightReporter /><JoinPage /></>} />
            <Route path="/jobs"             element={<PublicJobsPage />} />
            <Route path="/guest-names/:token" element={<GuestNamesPage />} /> {/* [marker: guest-names v1] */}
            <Route path="/thank-you"        element={<ThankYouPage />} /> {/* [marker: confirm-polish v1] after payment */}
            <Route path="/ticket/:token"    element={<TicketPage />} /> {/* [marker: website-feed v1] Show my ticket */}
            <Route path="/embed/jobs"       element={<><IframeHeightReporter /><PublicJobsPage embedded /></>} />

            {/* Member portal */}
            <Route path="/portal/login"            element={<PortalLoginPage />} />
            <Route path="/portal/forgot-password"  element={<PortalForgotPasswordPage />} />
            <Route path="/portal/set-password"     element={<PortalSetPasswordPage />} />
            <Route path="/portal" element={<PortalShell />}>
              <Route index                  element={<PortalDashboard />} />
              <Route path="profile"         element={<PortalProfilePage />} />
              <Route path="invoices"        element={<PortalInvoicesPage />} />
              <Route path="invoices/:id"    element={<PortalInvoiceDetailPage />} />
              <Route path="events"          element={<PortalEventsPage />} />
              <Route path="tickets"         element={<PortalTicketsPage />} />
              <Route path="jobs"            element={<PortalJobsPage />} />
            </Route>

            <Route path="/kiosk/:eventId" element={<RequireAuth><CheckInKioskPage /></RequireAuth>} />
            <Route path="/" element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route index                        element={<DashboardPage />} />
              <Route path="members"               element={<MembersPage />} />
              <Route path="members/:id"           element={<MemberDetailPage />} />
              <Route path="contacts"              element={<ContactsPage />} />
              <Route path="contacts/:id"          element={<ContactDetailPage />} />
              <Route path="contact-tags"          element={<ContactTagsSettingsPage />} />
              <Route path="events"                element={<EventsPage />} />
              <Route path="events/:id"            element={<EventDetailPage />} />
              <Route path="invoices"              element={<InvoicesPage />} />
              <Route path="invoices/new"          element={<NewInvoicePage />} />
              <Route path="invoices/:id"          element={<InvoiceDetailPage />} />
              <Route path="products"              element={<ProductsPage />} />
              <Route path="bookkeeping"           element={<BookkeepingPage />} />
              <Route path="bookkeeping-reports"   element={<BookkeepingReportsPage />} />
              <Route path="tasks"                 element={<TasksPage />} />
              <Route path="job-board"             element={<JobBoardAdminPage />} />
              <Route path="sponsor-links"         element={<SponsorLinksPage />} />
              <Route path="badges"                element={<BadgeDesignerPage />} />
              <Route path="website-photos"        element={<WebsitePhotosPage />} /> {/* [marker: website-feed v1] */}
              <Route path="events/:id/badges"     element={<BadgeDesignerPage />} />
              <Route path="reports"               element={<ReportsPage />} />
              <Route path="reports/builder"       element={<ReportBuilderPage />} />
            <Route path="settings/forms"        element={<RegistrationFormSettingsPage />} />
            <Route path="promo-codes"          element={<PromoCodesPage />} />
            <Route path="event-display"        element={<EventDisplayPage />} />
            <Route path="transactions"        element={<TransactionsPage />} />
            <Route path="renewals"             element={<RenewalsPage />} />
            <Route path="renewals/templates"   element={<RenewalTemplatesPage />} />
            <Route path="renewals/:id"         element={<RenewalBatchPage />} />
              <Route path="email"                 element={<EmailBlastsPage />} />
              <Route path="email-templates"       element={<EmailTemplatesPage />} />
              <Route path="letters"               element={<LettersPage />} />
              <Route path="imports"               element={<ImportsPage />} />
              <Route path="settings"              element={<SettingsPage />} />
              <Route path="staff"                 element={<RequireSuperAdmin><StaffPage /></RequireSuperAdmin>} />
              <Route path="permissions"           element={<RequireSuperAdmin><PermissionsPage /></RequireSuperAdmin>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </PortalAuthProvider>
    </AuthProvider>
  );
}
